// frontend/src/hooks/useFollowUpPopup.js

/**
 * Hook that wraps the "after marking contacted, what about a reminder?"
 * decision tree. Shared between LeadCard and LeadDetailsModal so the
 * trigger semantics stay consistent across surfaces.
 *
 * Decision tree (executed by ``triggerForLead``), and the OUTCOME it
 * resolves to - ``{ outcome, closed }``:
 *
 *   0. plan without reminders       -> 'noop'
 *   1. popup_dismissed=true         -> 'noop' (user opted out forever).
 *   2. default_days_offset/hour set -> auto-create reminder silently:
 *                                     'auto_created'. Replays
 *                                     {days_offset, hour, minute}; on ANY
 *                                     422 it falls back to (3).
 *   3. otherwise                    -> open the dialog: 'dialog_opened'
 *                                     (caller renders the
 *                                     FollowUpReminderDialog with the
 *                                     returned ``dialogLeadId``).
 *   *. prefs fetch / silent create failed (not a 422) -> 'error'.
 *
 * ``closed`` is a promise that resolves once nothing of this trigger is left
 * on screen: immediately for every outcome but 'dialog_opened', and on the
 * dialog's close for that one - WHATEVER the reason ("confirmed",
 * "dismissed", "gone", a 403). It also resolves if the component that owns
 * the hook unmounts with the dialog still open, so a caller waiting on it can
 * never wait forever. It never rejects.
 *
 * It resolves to ``{ reminderId }``: the id of the reminder THIS trigger
 * created (the silent auto-create, or the dialog's confirm - reported through
 * ``onDialogSuccess``), or null when it created none. The dashboard's undo of
 * a "contacted" mark uses it to cancel exactly the reminder that mark made.
 *
 * The dashboard reads it to decide WHEN a card marked "contacted" may leave
 * the list: never while its reminder dialog is open (the dialog is rendered
 * inside the card, so removing the card would tear the dialog down mid-use).
 *
 * Preferences are cached at module scope so multiple LeadCards on the
 * same page share one network round-trip. The cache is invalidated
 * via ``invalidateFollowUpPreferences()`` after the user interacts
 * with the popup (e.g. checks "remember my choice"), so the next
 * trigger sees fresh state.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import followUpService from "../services/followUp";
import { upsertPendingReminder } from "./usePendingReminders";

// Module-level cache: the prefs are user-scoped and immutable for the
// duration of a render, but we still need to refresh after the user
// interacts with the popup (remember_choice / dismiss_popup mutate
// the same row server-side).
//
// The prefs are one user's: AuthContext calls `resetFollowUpPreferences()` on
// logout and whenever the signed-in user id changes, so a second user on the
// same tab never inherits the first user's remembered slot or opt-out.
let _prefsPromise = null;

function getPreferencesCached() {
  if (_prefsPromise === null) {
    const request = followUpService.getPreferences().catch((err) => {
      // Don't poison the cache - next call retries. Only clear OUR entry: a
      // reset may have already replaced it with a newer request.
      if (_prefsPromise === request) _prefsPromise = null;
      throw err;
    });
    _prefsPromise = request;
  }
  return _prefsPromise;
}

export function invalidateFollowUpPreferences() {
  _prefsPromise = null;
}

/** Logout / user switch: forget the cached prefs (same effect, named for it). */
export function resetFollowUpPreferences() {
  _prefsPromise = null;
}

/** The trigger outcomes, as constants for the callers that branch on them. */
export const FOLLOW_UP_OUTCOME = Object.freeze({
  NOOP: "noop",
  AUTO_CREATED: "auto_created",
  DIALOG_OPENED: "dialog_opened",
  ERROR: "error",
});

const NO_REMINDER = Object.freeze({ reminderId: null });
const SETTLED = Promise.resolve(NO_REMINDER);
const settledWith = (outcome, reminder = null) => ({
  outcome,
  closed:
    reminder && reminder.id != null
      ? Promise.resolve({ reminderId: String(reminder.id) })
      : SETTLED,
});

/**
 * @param {Object} options
 * @param {(message: string) => void} [options.onSilentSuccess] - Called
 *   when a reminder was auto-created from a remembered slot. Receives
 *   a pre-formatted Hebrew/English message suitable for a toast.
 * @param {(err: Error) => void} [options.onError] - Called when the
 *   prefs fetch OR the silent reminder POST fails. Caller decides
 *   whether to surface a toast.
 * @param {boolean} [options.isHebrew=false]
 */
export default function useFollowUpPopup({
  onSilentSuccess,
  onError,
  isHebrew = false,
} = {}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLeadId, setDialogLeadId] = useState(null);

  // The resolver of the open dialog's `closed` promise, or null. Only ever one:
  // opening a second dialog first releases whoever waited on the first.
  const releaseRef = useRef(null);
  // The reminder the open dialog created, if its confirm went through.
  const createdRef = useRef(null);
  const releaseWaiter = useCallback(() => {
    const release = releaseRef.current;
    const reminderId = createdRef.current;
    releaseRef.current = null;
    createdRef.current = null;
    if (release) release(reminderId ? { reminderId } : NO_REMINDER);
  }, []);

  /** Open the dialog and hand back the promise that settles on its close. */
  const openDialog = useCallback(
    (leadId) => {
      releaseWaiter();
      const closed = new Promise((resolve) => {
        releaseRef.current = resolve;
      });
      setDialogLeadId(leadId);
      setDialogOpen(true);
      return { outcome: FOLLOW_UP_OUTCOME.DIALOG_OPENED, closed };
    },
    [releaseWaiter]
  );

  // Unmounting with the dialog open (the owner left the screen) must not leave
  // a caller waiting for a close that can no longer happen.
  useEffect(() => releaseWaiter, [releaseWaiter]);

  /**
   * Call after a successful submitFeedback() with feedback_type='contacted'.
   * The dialog state is managed inside the hook - the caller just
   * renders <FollowUpReminderDialog open={dialogOpen} leadId={dialogLeadId} ... />
   * and forwards the hook's onDialogClose handler.
   *
   * @param {string} leadId
   * @returns {Promise<{outcome: string, closed: Promise<void>}>} never rejects.
   */
  const triggerForLead = useCallback(
    async (leadId) => {
      let prefs;
      try {
        prefs = await getPreferencesCached();
      } catch (err) {
        if (onError) onError(err);
        return settledWith(FOLLOW_UP_OUTCOME.ERROR);
      }

      // (0) plan doesn't include follow-up reminders (paid Solo) - silent
      // no-op. The server is authoritative (the create endpoint 403s); this
      // just hides the popup so those users never see a feature they can't use.
      if (!prefs || prefs.follow_up_eligible === false) {
        return settledWith(FOLLOW_UP_OUTCOME.NOOP);
      }

      // (1) opted-out forever - silent no-op
      if (prefs.popup_dismissed) return settledWith(FOLLOW_UP_OUTCOME.NOOP);

      // (2) remembered slot - auto-create, no popup
      if (
        prefs.default_days_offset != null &&
        prefs.default_hour != null
      ) {
        try {
          // Replays the remembered day offset + time. `minute` is always
          // sent: the server reports a legacy (pre-fur002) choice as 0.
          const reminder = await followUpService.createReminder(leadId, {
            days_offset: prefs.default_days_offset,
            hour: prefs.default_hour,
            minute: prefs.default_minute ?? 0,
            remember_choice: false,  // already remembered
            dismiss_popup: false,
          });
          if (reminder && reminder.scheduled_for) {
            upsertPendingReminder(leadId, reminder.scheduled_for);
          }
          if (onSilentSuccess) {
            onSilentSuccess(
              isHebrew
                ? "תזכורת למעקב נקבעה לפי בחירתך השמורה"
                : "Reminder set (saved choice)"
            );
          }
          return settledWith(FOLLOW_UP_OUTCOME.AUTO_CREATED, reminder);
        } catch (err) {
          // ANY 422 (a window code, or a remembered choice the server no
          // longer accepts - e.g. "1 day at 00:15" replayed after midnight
          // lands in the past): ask instead of failing silently.
          if (err && err.response && err.response.status === 422) {
            return openDialog(leadId);
          }
          if (onError) onError(err);
          return settledWith(FOLLOW_UP_OUTCOME.ERROR);
        }
      }

      // (3) no prior choice - open the dialog
      return openDialog(leadId);
    },
    [onSilentSuccess, onError, isHebrew, openDialog]
  );

  /**
   * Forwarded to the dialog's onClose. action is "confirmed" (reminder
   * created via the dialog's confirm button) or "dismissed" (X / Maybe
   * later). Either way the prefs cache is invalidated because both
   * actions can mutate server-side state.
   */
  const onDialogClose = useCallback(() => {
    setDialogOpen(false);
    setDialogLeadId(null);
    invalidateFollowUpPreferences();
    releaseWaiter();
  }, [releaseWaiter]);

  /**
   * Forwarded from the dialog's onSuccess(reminder) - it fires right BEFORE
   * onClose("confirmed"), so the open trigger's `closed` can carry the id.
   * Harmless without an open trigger.
   */
  const onDialogSuccess = useCallback((reminder) => {
    if (releaseRef.current && reminder && reminder.id != null) {
      createdRef.current = String(reminder.id);
    }
  }, []);

  // Memoized: LeadCard's feedback handler chain depends on this object, so a
  // fresh object per render invalidated those useCallbacks on every render.
  return useMemo(
    () => ({
      triggerForLead,
      dialogOpen,
      dialogLeadId,
      onDialogClose,
      onDialogSuccess,
    }),
    [triggerForLead, dialogOpen, dialogLeadId, onDialogClose, onDialogSuccess]
  );
}
